import { useEffect } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, LineChart, Sprout, FileText, Handshake, Sliders, FileSearch, Users, Mail, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: LineChart },
  { to: "/admin/growth", label: "Organic Growth", icon: Sprout },
  { to: "/admin/articles", label: "Articles & Questions", icon: FileText },
  { to: "/admin/offers", label: "Affiliate Offers", icon: Handshake },
  { to: "/admin/merchandising", label: "Merchandising", icon: Sliders },
  { to: "/admin/sources", label: "Sources & Verification", icon: FileSearch },
  { to: "/admin/email", label: "Email Marketing", icon: Users },
  { to: "/admin/contact", label: "Contact Messages", icon: Mail },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  // Bookmarked admin pages should read as the admin area, not as whichever public page was open
  // last. The tab title is the only thing this changes.
  useEffect(() => {
    document.title = "Admin — Human Home Living";
  }, []);

  if (user === null)
    return <div className="min-h-screen bg-bone flex items-center justify-center text-[13px] text-[#6E6A63]">Checking session…</div>;
  if (user === false)
    return <Navigate to="/admin/login" replace state={{ from: pathname }} />;

  return (
    <div className="min-h-screen bg-bone flex flex-col lg:flex-row" data-testid="admin-shell">
      <aside className="lg:w-[260px] bg-charcoal text-white lg:min-h-screen shrink-0">
        <div className="px-6 py-7 border-b border-white/10">
          <Link to="/" className="display text-[19px] leading-tight">
            Human Home Living
          </Link>
          <div className="text-[9.5px] tracking-[0.22em] uppercase text-sage mt-1">Admin & CMS</div>
        </div>
        <nav className="py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3.5 text-[13.5px] font-light transition-colors ${
                  isActive ? "bg-white/10 text-white border-l-2 border-sage" : "text-[#A8A5A0] hover:text-white border-l-2 border-transparent"
                }`
              }
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-white/10 mt-auto">
          <Link
            to="/"
            className="flex items-center gap-2 text-[12.5px] font-light text-[#A8A5A0] hover:text-white transition-colors mb-4"
          >
            <ExternalLink size={14} /> View public site
          </Link>
          <div className="text-[11.5px] font-light text-[#9E9B95] mb-3">{user.email}</div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-[12px] tracking-[0.12em] uppercase text-[#A8A5A0] hover:text-white transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

export const AdminPage = ({ title, lede, children, actions }) => (
  <div className="px-6 md:px-10 py-9 md:py-12">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-9">
      <div>
        <h1 className="display text-[32px] md:text-[40px] leading-tight text-charcoal">{title}</h1>
        {lede && <p className="text-[14.5px] font-light text-[#6A665F] mt-2.5 max-w-2xl leading-relaxed">{lede}</p>}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

export const Pill = ({ children, tone = "neutral" }) => {
  const tones = {
    neutral: "border-line text-[#7A766F] bg-white",
    green: "border-[#22C55E]/40 text-[#166534] bg-[#22C55E]/10",
    yellow: "border-[#EAB308]/45 text-[#854D0E] bg-[#EAB308]/12",
    orange: "border-[#F97316]/40 text-[#9A3412] bg-[#F97316]/10",
    red: "border-[#EF4444]/35 text-[#991B1B] bg-[#EF4444]/10",
    sage: "border-sage/30 text-sage-dark bg-sage-soft",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10.5px] tracking-[0.1em] uppercase border ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const statusTone = (s) =>
  ({
    PUBLISHED: "green",
    ACTIVE: "green",
    APPROVED: "green",
    READY: "sage",
    READY_TO_APPLY: "sage",
    REVIEW: "yellow",
    APPLIED: "yellow",
    DRAFT: "yellow",
    PAUSED: "orange",
    RESEARCH: "neutral",
    RESEARCHING: "neutral",
    IDEA: "neutral",
    ARCHIVED: "neutral",
    REJECTED: "red",
  }[s] || "neutral");
